import { compose, mdsWidget } from 'dojo/_base/declare';
import template from './BlockEditor.html?raw';

import _StatefulSection from '../../../moreCore/UI/_StatefulSection/_StatefulSection';
import _uiFrameMixin from '../../../moreCore/UI/Container/uiFrame';
import NumberInput from '../../../moreCore/UI/NumberInput/NumberInput';
import HintBox from '../../../moreCore/UI/HintBox/HintBox';
import { TabContainer } from "../../../moreCore/_loadingHelpers/Tabbing";
import { Block, HtBlockEvBooking } from "../ScreenplayTypes";
import lang from 'dojo/_base/lang';
import GenericDialog from '../../../moreCore/UI/GenericDialog/GenericDialog';
import { ExpandableSection, ExpandableSectionMarkings } from "../../../moreCore/UI/Container/ExpandableSectionTypes";
import EntityStore from '../../../moreCore/Tools/EntityStore';
import StaticTable from '../../../moreCore/UI/StaticTable/StaticTable';
import _GlobalSelectEntityDialogsMixin from '../../_loadingHelpers/_GlobalSelectEntityDialogsMixin';
import { SelectEntityDialogTypes } from "../../../moreCore/_loadingHelpers/SelectEntityDialog";
import SearchSelect from '../../../moreCore/UI/SearchSelect/SearchSelect';
import { uiFrameTypes } from "../../../moreCore/UI/Container/uiFrameTypes";
import DateTimeInput from '../../../moreCore/UI/DateTimeInput/DateTimeInput';

class BlockEditor extends compose(_StatefulSection, _uiFrameMixin, _GlobalSelectEntityDialogsMixin) {
    templateString: any = template;

    private selectBookingMode: "selectEvBookingImage" | "selectEvBookingList" = "selectEvBookingImage";

    private inputInterval: MdsLax<InstanceType<typeof NumberInput>>;
    private inputFilterEntity1: MdsLax<InstanceType<typeof HintBox>>;
    private inputFilterEntity2: MdsLax<InstanceType<typeof HintBox>>;
    private inputFilterEntity3: MdsLax<InstanceType<typeof HintBox>>;
    private inputFilterEntity4: MdsLax<InstanceType<typeof HintBox>>;

    private inputTemplate: MdsLax<InstanceType<typeof SearchSelect>>;
    private inputType: MdsLax<InstanceType<typeof SearchSelect>>;
    private inputFallbacktype: MdsLax<InstanceType<typeof SearchSelect>>;
    private inputDisplaymode: MdsLax<InstanceType<typeof SearchSelect>>;

    private tableContainer: MdsLax<InstanceType<typeof ExpandableSection>>;
    private store: MdsLax<InstanceType<typeof EntityStore<HtBlockEvBooking>>>;
    private table: MdsLax<InstanceType<typeof StaticTable>>;
    private btnNewBooking: HTMLElement;

    private inputPreviewDate: MdsLax<InstanceType<typeof DateTimeInput>>;

    isNew: boolean = false;
    reloadAfterSave = true;

    setReloadAfterSave(reload: boolean) {
        this.reloadAfterSave = reload;
    }

    private rowConfig: StaticTable.RowConfig = {
        attributes: [
            //"order",
            "ref_ev_booking.gDisp_1"
        ],
        headings: [
            //this.translate("General_Label_Order"),
            (this as any). translate("General_Label_EvBooking")
        ],
        colNamesInternal: [
            //"order",
            "ref_ev_booking.gDisp_1"
        ],
        bgColors: [
            //null,
            "ref_ev_booking.ref_ev_activity_status.coloration"
        ],
        colspans: [
            //1,
            5
        ]
    }

    private rowConfigPreview: StaticTable.RowConfig = {
        attributes: [
            //"order",
            "gDisp_1"
        ],
        headings: [
            //this.translate("General_Label_Order"),
            (this as any).translate("General_Label_EvBooking")
        ],
        colNamesInternal: [
            //"order",
            "gDisp_1"
        ],
        bgColors: [
            //null,
            "ref_ev_activity_status.coloration"
        ],
        colspans: [
            //1,
            5
        ]
    }

    private tableLayout: StaticTable.Layout = {
        name: "table",
        label: "table",
        rows: [(this as any).rowConfig],
    }

    private tableLayoutPreview: StaticTable.Layout = {
        name: "tablePreview",
        label: "tablePreview",
        rows: [(this as any).rowConfigPreview],
    }

    private setupTable() {
        this.store = new EntityStore();
        this.store.on("change", lang.hitch(this, this._onStoreChange));
        this.table = new StaticTable();
        //if (this.isNew == true){ //NOTE - HOTFIX 2025-10-31: das layout scheint nie richtig gesetzt worden sein
        //    this.table.setLayout(this.tableLayout);
        //}else{
        //    this.table.setLayout(this.tableLayoutPreview);
        //}
        this.table.addDefaultRowActionButton("rowsorting", null, lang.hitch(this, this._showActionButtons));
        this.table.addDefaultRowActionButton("delete", null, lang.hitch(this, this._showActionButtons));
        this.table.setupFromStore(this.store);
        this.table.setCyAttribute("BlockEditorTable");
        this.table.placeAt(this.tableContainer);
        this.setTableType(null);
    }

    private _onStoreChange() {
        this._setExpanderMarking();
        let diffData = this.store.getDiffData();
        this.setAttributeValue("evBookingsDiffData", diffData);
    }

    private _tableHasContent(): boolean {
        return this.store.getCurrentCompleteList().length > 0;
    }

    private _setExpanderMarking(): void {
        this.tableContainer.setMarking(this._tableHasContent() ? ExpandableSectionMarkings.hasContent : ExpandableSectionMarkings.default);
    }

    private _showActionButtons(): boolean {
        let val1 = this.getAttributeValue("type").value; // Type muss individuell sein im dropdown drueber im editor....
        let val2 = this.getAttributeValue("type");
        return val1 === "custom" || val2 === "custom";
    }

    setIsNew(isNew: boolean): void {
        this.isNew = isNew;
        this.enableButton("save");
    }

    setup(tabContainer?: MdsLax<InstanceType<typeof TabContainer>>, entity?: Block, mode: uiFrameTypes.DisplayMode = "tab", node?: HTMLElement): void {
        let heading;


        this.setupTable();

        if (entity) {
            this.internalName = "blockEditor_" + entity.ID;
            if (entity.is_new) {
                this.setData(entity);
                heading = this.translate("Screenplay_Heading_NewBlock")
            } else {
                heading = `${this.translate("Screenplay_Heading_Block")}: ${entity.description}`;
                entity.is_new = false;
                this.setStartupState("ready");
                this._loadEntity(entity.ID);
            }
        } else {
            this.internalName = "BlockEditor_New";
            let newEntity: Block = {
                ID: "",
                $Displayname: "",
                Classname: "",
                ClassPrintname: "",
                description: "",
                filter_file: "",
                type: "",
                display_mode: "",
                content_change_interval: null,
                ref_filterparameter_entity_1: null,
                ref_filterparameter_entity_2: null,
                ref_filterparameter_entity_3: null,
                ref_filterparameter_entity_4: null,
                EL_ht_screenplay_block_ev_booking: [],
                is_new: true
            }
            entity = newEntity;
            this.setData(entity);
            heading = this.translate("Screenplay_Heading_NewBlock")
        }

        if (tabContainer && mode == "tab") {
            this.show(mode, heading, tabContainer.name);
        } else if (node && mode == "node") {
            this.show(mode, heading, null, node);
        } else {
            this.show("dlg", heading, tabContainer.name);
        }

        this.createDefaultButton("save", lang.hitch(this, this._onClickSave), this.isNew, "primary");
    }

    private _loadEntity(entityId: string): void {
        this.StandbyStart();
        this._deactivateDataButtons(true, true);
        this.RequestManager.callfunction(
            "Webtool_API_Screenplay__Block_LoadEntity",
            { ID: entityId },
            lang.hitch(this, this._onLoadEntityResponse),
            lang.hitch(this, this._onLoadEntityError));
    }

    private _onLoadEntityResponse(response: Block): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        response.is_new = false;
        this.setData(response);
    }

    private _onLoadEntityError(error): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.error(error);
    }

    private _onClickSave(evt: MouseEvent, isBeforeClose: boolean): void {
        this.StandbyStart();
        this._deactivateDataButtons(true, true);

        this.emit("clickedSave");

        let entityId = this.getAttributeValue("ID");
        let classname = this.getAttributeValue("Classname");
        let isNew = this.getAttributeValue("is_new");
        let changedData = this.getChangedData();
        this.RequestManager.callfunction(
            "Webtool_API_Screenplay__Block_SaveEntity",
            { ID: entityId, Classname: classname, is_new: isNew, changedData: changedData },
            lang.hitch(this, this._onSaveResponse, isBeforeClose),
            lang.hitch(this, this._onSaveError));
    }

    private _onSaveResponse(isBeforeClose: boolean = false, response): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.debug(response);
        this.emit("changed");
        this.emit("saved");
        if (this.reloadAfterSave) this._loadEntity(response.entityId);
    }

    private _onSaveError(error): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.error(error);
    }

    private _onClickRollback(e): void {
        this.rollback();
    }

    private _onClickReload(e): void {
        let entity = this.getAttributeValue("___root");
        this._loadEntity(entity.ID);
    }

    private _onClickDelete(e): void {
        this.StandbyStart();
        this._deactivateDataButtons(true, true);
        let entityId = this.getAttributeValue("ID");
        let classname = this.getAttributeValue("Classname");
        this.RequestManager.callfunction(
            "Webtool_API_Screenplay__Block_DeleteEntity",
            { ID: entityId, Classname: classname },
            lang.hitch(this, this._onDeleteResponse),
            lang.hitch(this, this._onDeleteError));
    }

    private _onDeleteResponse(response): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.debug(response);
        this.emit("changed");
        this.close();
    }

    private _onDeleteError(error): void {
        this.StandbyStop();
        this._deactivateDataButtons(false, true);
        console.error(error);
    }

    private _deactivateDataButtons(deactivate: boolean, deactivateAll?: boolean): void {
        if (!this.isNew) this.requestContextButtonDisable("save", deactivate);
        this.requestContextButtonDisable("rollback", deactivate);
        if (deactivateAll) {
            this.requestContextButtonDisable("delete", deactivate);
            this.requestContextButtonDisable("reload", deactivate);
        }
    }

    private _onClickSEDButton(mode: string, input: MdsLax<InstanceType<typeof HintBox>>, evt: MouseEvent) {
        this.openSelectEntityDialog(evt, mode, lang.hitch(this, this._onSEDCallback, input, evt, null));
    }

    private _onSEDCallback(input: MdsLax<InstanceType<typeof HintBox>>, evt: MouseEvent, labelAttribute: string, mode: string, data: Array<string>, items: Array<SelectEntityDialogTypes.Item<more.EntityAPIResultExtendedGDisp1 | {value: string, label: string}>>) {
        switch (mode) {
            case "selectClass":
                if (items && items.length > 0) {
                    let values = items[0].entry.value.split("-");
                    let classname = values[0];
                    let labelAttribute = values[1];
                    this.openSelectEntityDialog(evt, "selectEntity", lang.hitch(this, this._onSEDCallback, input, evt, labelAttribute), null, { classname: classname });
                }
                break;
            case "selectEntity":
                if (items && items.length > 0) {
                    let entity = items[0].entry.data;
                    //input.setLabelAttribute(labelAttribute);
                    input.setValue(entity);
                } else {
                    input.setValue(null);
                }
                break;
            case "selectEvBookingImage":
            case "selectEvBookingList":
                if (items && items.length > 0) {
                    for (let item of items) {
                        let entity = item.entry.data as more.EntityAPIResultExtendedGDisp1;
                        if (this._storeHasBooking(entity)) {
                            this.showToast(this.translate("BlockEditor_Warning_BookingInStore") + " - " + entity.gDisp_1, "fas fa-exclamation-triangle");
                        } else {
                            let newOrder = this.store.getCurrentCompleteList().length;
                            let newEntity: HtBlockEvBooking = {
                                $Displayname: "",
                                Classname: "",
                                ClassPrintname: "",
                                ID: "",
                                order: newOrder,
                                ref_ev_booking: entity,
                                ref_screenplay_block: null
                            }
                            this.store.addEntry(newEntity);
                        }
                    }
                }
                break;
        }
    }

    private _storeHasBooking(evBooking: more.EntityAPIResultExtended) {
        let completeList = this.store.getCurrentCompleteList();
        for (let storeEntity of completeList) {
            if (storeEntity.ref_ev_booking.ID === evBooking.ID) return true;
        }
        return false;
    }

    private _onClickNewBooking(evt: MouseEvent) {
        this.openSelectEntityDialog(evt, this.selectBookingMode, lang.hitch(this, this._onSEDCallback, null, evt, null));
    }

    _onCloseRequest() {
        let hasChanged = this.isDirty() === true;

        if (hasChanged) {
            let dlgConfirm = new GenericDialog();
            dlgConfirm.show(this.translate("Screenplay_Block_AskSavePendingChanges"),
                this.translate("General_PendingChanges") + " - " + this.title,
                "",
                "",
                "yesnocancel",
                lang.hitch(this, this._confirmCallback));

            return false;

        } else {
            return true;
        }
    }

    private _confirmCallback(result) {
        if (result == -1) { // nein
            this.emit("notSaved");
            this.close();
        } else if (result == 0) { // cancel
            this.emit("canceled");
        }
        else {
            this._onClickSave(null, true);
            this.on("saved", lang.hitch(this, this.close));
        }
    }

    private _onChangeDisplaymode(displayMode: "list" | "image") {
        switch (displayMode) {
            default:
            case "image":
                this.selectBookingMode = "selectEvBookingImage";
                break;
            case "list":
                this.selectBookingMode = "selectEvBookingList";
                break;
        }
    }

    protected afterSetData(): void {
        super.afterSetData.apply(this, arguments as any);

        let isNew = this.getAttributeValue("is_new");
        if (!isNew) {
            this.createDefaultButton("delete", lang.hitch(this, this._onClickDelete), true, "secondary");
            this.createDefaultButton("rollback", lang.hitch(this, this._onClickRollback), false, "secondary");
            this.createDefaultButton("reload", lang.hitch(this, this._onClickReload), true, "primary");
            this.changeInternalName("BlockEditor_" + this.getAttributeValue("ID"));
            this.setTitles(`${this.translate("Screenplay_Heading_Block")}: ${this.getAttributeValue("description")}`);

            let bookings = this.getAttributeValue("EL_ht_screenplay_block_ev_booking");
            this.store.setup(bookings);
            this._setExpanderMarking();
        } else {

        }
        this._deactivateDataButtons(true);
        //if (this.isNew) this.requestContextButtonDisable("save", false);

        //let bookings = this.getAttributeValue("EL_ht_screenplay_block_ev_booking");
        //this.store.setup(bookings);
        //this._setExpanderMarking();
        let date = new Date();
        this.inputPreviewDate.setValue(date, true);
        this.inputPreviewDate._value.offsetMinutes = date.getTimezoneOffset();

        let value = this.getAttributeValue("type");
        let label = this.getAttributeValue("$type");
        let type = { value, label };
        this.inputType.setValue(value);
        this.setTableType(value);
        //this._loadPreviewBookings();


        let displayMode = this.getAttributeValue("display_mode");
        this._onChangeDisplaymode(displayMode);
        //this.inputDisplaymode.setValue(displayMode);

        this.setAttributeValue("evBookingsDiffData", null);

        this.emit("dataSet");
    }

    private setTableType(type: string) {
        if (type === "custom") {
            this.table.removeLayout(this.tableLayoutPreview);
            this.table.setLayout(this.tableLayout);
            this.hideNode(this.tableContainer, false);
            this.hideNode(this.btnNewBooking, false);
            this.hideNode(this.inputPreviewDate, true);
            let bookings = this.getAttributeValue("EL_ht_screenplay_block_ev_booking");
            this.store.setup(bookings);
            this._setExpanderMarking();
        } else if ((type === "tonight" || type === "promo" || type === "promo_group" || type === "tonight_internal" || type === "today_backstage" || type === "orchestra" || type === "occupation" || type === "planning_mainrooms") && !this.isNew) {
            this.table.removeLayout(this.tableLayout);
            this.table.setLayout(this.tableLayoutPreview);
            this.hideNode(this.tableContainer, false);
            this.hideNode(this.btnNewBooking, true);
            this.hideNode(this.inputPreviewDate, false);
            this.setAttributeValue("evBookingsDiffData", null);
            this.store.setup([]);
            this._setExpanderMarking();
            this._loadPreviewBookings();
        } else {
            this.hideNode(this.tableContainer, true);
            this.setAttributeValue("evBookingsDiffData", null);
            this.store.setup([]);
        }

        if (type === "promo_group") {
            this.hideNode(this.inputFilterEntity1, false);
            //this.hideNode(this.inputPreviewDate, true);
        }
        else this.hideNode(this.inputFilterEntity1, true);
    }

    protected _onChanged(attribute: string, newValue: any): void {
        switch (attribute) {
            case "type":
                this.setTableType(newValue?.value);

                //if (newValue && newValue.value === "custom") {
                //    this.table.removeLayout(this.tableLayoutPreview);
                //    this.table.setLayout(this.tableLayout);
                //    this.hideNode(this.tableContainer, false);
                //    this.hideNode(this.btnNewBooking, false);
                //    this.hideNode(this.inputPreviewDate, true);
                //    let bookings = this.getAttributeValue("EL_ht_screenplay_block_ev_booking");
                //    this.store.setup(bookings);
                //    this._setExpanderMarking();
                //} else if (newValue && (newValue.value === "tonight" || newValue.value === "promo" || newValue.value === "promo_group" || newValue.value === "tonight_internal" || newValue.value === "today_backstage" || newValue.value === "orchestra") && !this.isNew) {
                //    this.table.removeLayout(this.tableLayout);
                //    this.table.setLayout(this.tableLayoutPreview);
                //    this.hideNode(this.tableContainer, false);
                //    this.hideNode(this.btnNewBooking, true);
                //    this.hideNode(this.inputPreviewDate, false);
                //    this.setAttributeValue("evBookingsDiffData", null);
                //    this.store.setup([]);
                //    this._setExpanderMarking();
                //    this._loadPreviewBookings();
                //} else {
                //    this.hideNode(this.tableContainer, true);
                //    this.setAttributeValue("evBookingsDiffData", null);
                //    this.store.setup([]);
                //}

                //if (newValue && newValue.value === "promo_group") {
                //    this.hideNode(this.inputFilterEntity1, false);
                //    //this.hideNode(this.inputPreviewDate, true);
                //}
                //else this.hideNode(this.inputFilterEntity1, true);
                break;
            case "fallbacktype":
                this._loadPreviewBookings();
                break;
            case "ref_filterparameter_entity_1":
                this._loadPreviewBookings();
                break;
            case "display_mode":
                if (newValue) {
                    this._onChangeDisplaymode(newValue.value);
                }
        }
    }

    private _onChangePreviewDate(): void {
        let type = this.getAttributeValue("type");

        this._loadPreviewBookings();
    }

    private _loadPreviewBookings(): void {

        let type = this.getAttributeValue("type");
        if ((type && type.value === "custom") || type === "custom") return;

        this.store.setup([]);
        this.setAttributeValue("evBookingsDiffData", null);

        let previewDate = this.inputPreviewDate.getValue();

        let typeData;
        if (typeof type === "object") typeData = type;
        else typeData = { value: type, label: this.getAttributeValue("$type") };

        if (previewDate) {
            let data = {
                date: previewDate, type: typeData, fallbacktype: this.getAttributeValue("fallbacktype"), filterEntity1: this.getAttributeValue("ref_filterparameter_entity_1")
            };
            this.inputPreviewDate.StandbyStart();
            this.RequestManager.callfunction("Webtool_API_Screenplay__Block_GetPreviewBookings", data, lang.hitch(this, this._onLoadPreviewBookingsResponse), lang.hitch(this, this._onLoadPreviewBookingsError));
        }
    }

    private _onLoadPreviewBookingsResponse(response): void {
        this.inputPreviewDate.StandbyStop();
        this.store.setup(response);
        this._setExpanderMarking();
    }

    private _onLoadPreviewBookingsError(error): void {
        this.inputPreviewDate.StandbyStop();
        console.error(error);
    }

    protected onDirtyChange(newState: boolean) {
        this._deactivateDataButtons(!newState);
        this.setDirtyMarker(newState);
    }

    postCreate(): void {
        super.postCreate.apply(this, arguments as any);

        this.inputInterval.setUnit("s");


        this.SEDconfigCallfunction = "Webtool_API_Screenplay__Block_GetSEDConfig";
        this.inputFilterEntity1.setInputNodeReadonly(true);
        this.inputFilterEntity2.setInputNodeReadonly(true);
        this.inputFilterEntity3.setInputNodeReadonly(true);
        this.inputFilterEntity4.setInputNodeReadonly(true);
        this.inputFilterEntity1.addInlineButton(lang.hitch(this, this.openSelectEntityDialog, null, "selectEntity", lang.hitch(this, this._onSEDCallback, this.inputFilterEntity1, null, "description"), null, { classname: "ev_distribution_channel" } ));
        this.inputFilterEntity2.addInlineButton(lang.hitch(this, this._onClickSEDButton, "selectClass", this.inputFilterEntity2));
        this.inputFilterEntity3.addInlineButton(lang.hitch(this, this._onClickSEDButton, "selectClass", this.inputFilterEntity3));
        this.inputFilterEntity4.addInlineButton(lang.hitch(this, this._onClickSEDButton, "selectClass", this.inputFilterEntity4));

        this.inputType.setCallfunction("Webtool_API_Screenplay__Block_GetTypes");
        this.inputType.loadListValues();
        this.inputFallbacktype.setCallfunction("Webtool_API_Screenplay__Block_GetFallbacktypes");
        this.inputFallbacktype.loadListValues();
        this.inputTemplate.setCallfunction("Webtool_API_Screenplay__Block_GetTemplates");
        this.inputTemplate.loadListValues();
        this.inputDisplaymode.setCallfunction("Webtool_API_Screenplay__Block_GetDisplayModes");
        this.inputDisplaymode.loadListValues();
    }

    startup(): void {
        super.startup.apply(this, arguments as any);
    }

}

export default mdsWidget(BlockEditor);
